import { FC, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { Input, Modal } from 'antd';

import { usePlanUsageLimits } from '@/hooks/usePlanUsageLimits';
import type { CreateSubteamInput } from '@/services/database/teams';
import { teamService } from '@/services/database/teams';
import { PlatformAlert } from '@/shared/components/alerts';
import { PlatformForm as Form } from '@/shared/components/forms';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { useTeamStore } from '@/stores';

export interface TeamCreationTarget {
    id: string;
    name: string;
    subteamCount?: number;
    subteams?: Array<{
        id: string;
        members?: readonly unknown[];
        name: string;
    }>;
}

interface CreateSubteamModalProps {
    open: boolean;
    team?: TeamCreationTarget;
    onClose: () => void;
}

const CreateSubteamModal: FC<CreateSubteamModalProps> = ({ onClose, open, team }) => {
    const [form] = Form.useForm<CreateSubteamInput>();
    const [submitting, setSubmitting] = useState(false);
    const { fetchTeams } = useTeamStore();
    const { getUsageState } = usePlanUsageLimits();
    const subteamUsage = getUsageState('subteamsPerTeam', team?.subteamCount ?? team?.subteams?.length ?? 0);

    useEffect(() => {
        if (!open) {
            form.resetFields();
        }
    }, [form, open]);

    const handleCreate = async () => {
        if (!team || submitting || subteamUsage.reached) {
            return;
        }

        const values = await form.validateFields();
        setSubmitting(true);

        try {
            await teamService.createSubteam(team.id, values);
            await fetchTeams();
            toast('Subteam Created', { description: values.name });
            onClose();
        } catch (error) {
            captureException(error, { extra: { teamId: team.id } });
            toast.error('Unable to create subteam', { description: 'Please try again.' });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            confirmLoading={submitting}
            destroyOnClose
            okButtonProps={{ disabled: !team || submitting || subteamUsage.reached }}
            okText='Create'
            open={open}
            title={team ? `Create Subteam in ${team.name}` : 'Create Subteam'}
            onCancel={onClose}
            onOk={() => void handleCreate()}
        >
            {subteamUsage.reached ? (
                <PlatformAlert
                    message={subteamUsage.reason}
                    showIcon
                    status='warning'
                    style={{ marginBottom: '0.75rem' }}
                />
            ) : null}
            <Form form={form} layout='vertical'>
                <Form.Item name='name' label='Name' rules={[{ required: true, message: 'Enter a subteam name' }]}>
                    <Input placeholder='Deal Team...' />
                </Form.Item>
                <Form.Item name='description' label='Description'>
                    <Input.TextArea placeholder='Optional Context For This Subteam...' rows={3} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default CreateSubteamModal;
